import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../config/firebase';
import { nanoid } from 'nanoid';
import { NoteImage } from '../types';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_WIDTH = 1280;
const MAX_HEIGHT = 720;
const JPEG_QUALITY = 0.65;

// Compress and resize image before upload
async function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

export const imageService = {
  // Upload an image for a note
  async uploadImage(noteId: string, file: File): Promise<NoteImage> {
    const user = auth.currentUser;
    if (!user) throw new Error('Must be logged in to upload images');

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('Image must be less than 5MB');
    }

    // Compress the image
    const compressedBlob = await compressImage(file);

    // Generate unique ID and path
    const imageId = nanoid();
    const extension = 'jpg'; // Always save as JPEG after compression
    const path = `users/${user.uid}/notes/${noteId}/${imageId}.${extension}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, compressedBlob, {
      contentType: 'image/jpeg',
      customMetadata: {
        originalName: file.name,
        noteId: noteId,
      },
    });

    // Get download URL
    const url = await getDownloadURL(storageRef);

    return {
      id: imageId,
      url,
      name: file.name,
      size: compressedBlob.size,
      createdAt: new Date(),
    };
  },

  // Delete an image
  async deleteImage(noteId: string, imageId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('Must be logged in to delete images');

    const path = `users/${user.uid}/notes/${noteId}/${imageId}.jpg`;
    const storageRef = ref(storage, path);

    try {
      await deleteObject(storageRef);
    } catch (error: any) {
      // Ignore if file doesn't exist
      if (error.code !== 'storage/object-not-found') {
        throw error;
      }
    }
  },

  // Delete all images for a note
  async deleteAllImages(noteId: string, images: NoteImage[]): Promise<void> {
    const deletePromises = images.map((img) => this.deleteImage(noteId, img.id));
    await Promise.all(deletePromises);
  },
};
